import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { FolderKanban, Clock, ArrowRight, CheckCircle2, FileText, User } from 'lucide-react';
import { PUBLISHING_STAGES_CONFIG } from '@ftt/shared';

export const ClientProjectsPage: React.FC = () => {
  const { data: projectsRes, isLoading } = useQuery({
    queryKey: ['client-projects'],
    queryFn: () => api.get('/client/projects').then((res) => res.data.data),
  });

  const projects = projectsRes || [];

  return (
    <div className="space-y-8">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
            Publishing Projects
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            View live status, editorial timeline milestones, and deliverables for your publications.
          </p>
        </div>

        <Link
          to="/submit-enquiry"
          className="px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs shadow-sm transition-colors self-start sm:self-auto"
        >
          + Submit New Project / Proposal
        </Link>
      </div>

      {/* Projects List */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-500">Loading your publishing projects...</div>
      ) : projects.length === 0 ? (
        <div className="p-12 rounded-2xl bg-white border border-editorial-border text-center space-y-4">
          <FolderKanban className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-800">No Projects Found</h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            You do not currently have any active publishing projects under your account.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {projects.map((proj: any) => {
            const currentStageConfig = PUBLISHING_STAGES_CONFIG.find((s) => s.status === proj.status);
            const completedMilestones = proj.milestones?.filter((m: any) => m.status === 'COMPLETED').length || 0;
            const totalMilestones = proj.milestones?.length || 12;
            const progress = Math.round((completedMilestones / totalMilestones) * 100);

            return (
              <div
                key={proj.id}
                className="p-6 sm:p-8 rounded-2xl bg-white border border-editorial-border shadow-xs hover:border-brand-300 transition-all space-y-6"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono px-2.5 py-0.5 rounded bg-slate-100 text-slate-700 font-bold uppercase">
                        {proj.projectCode}
                      </span>
                      <span className="text-xs text-brand-700 font-mono font-bold">
                        {proj.category}
                      </span>
                    </div>
                    <h2 className="font-serif text-2xl font-bold text-slate-900">
                      {proj.title}
                    </h2>
                    <p className="text-xs text-slate-500">
                      Author: <strong>{proj.author}</strong> &bull; Format: {proj.projectType}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="text-[10px] text-slate-400 font-mono uppercase block">Stage</span>
                      <span className="text-xs font-bold text-brand-800 bg-brand-50 px-3 py-1 rounded border border-brand-200 inline-block">
                        {currentStageConfig?.label || proj.status}
                      </span>
                    </div>
                    <Link
                      to={`/client/projects/${proj.id}`}
                      className="px-4 py-2.5 rounded-lg bg-editorial-dark hover:bg-slate-800 text-white font-bold text-xs transition-colors"
                    >
                      Open Roadmap &rarr;
                    </Link>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-mono">
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <span className="text-slate-400 block text-[10px] uppercase">Assigned Staff Lead</span>
                    <span className="font-bold text-slate-800">
                      {proj.assignedStaff ? `${proj.assignedStaff.firstName} ${proj.assignedStaff.lastName}` : 'Acquisitions Board'}
                    </span>
                  </div>

                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <span className="text-slate-400 block text-[10px] uppercase">Milestones Progress</span>
                    <span className="font-bold text-brand-800">
                      {completedMilestones} of {totalMilestones} Completed ({progress}%)
                    </span>
                  </div>

                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <span className="text-slate-400 block text-[10px] uppercase">Project Documents</span>
                    <span className="font-bold text-slate-800">
                      {proj.documentsCount || 0} Files in Document Centre
                    </span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div className="bg-brand-600 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};
